import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha,
} from '@mui/material';
import {
  TrendingUp as TrendingIcon,
  TrendingDown as TrendingDownIcon,
  Email as EmailIcon,
  Visibility as ViewIcon,
  TouchApp as ClickIcon,
  Block as BounceIcon,
  Cancel as UnsubscribeIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Timeline as TimelineIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

function CampaignAnalytics({ campaign }) {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (campaign) {
      loadAnalytics();
      loadRecipients();
    }
  }, [campaign]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/campaigns/${campaign._id}/analytics`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('tkn')}` },
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRecipients = async () => {
    try {
      const response = await fetch(`/api/v1/campaigns/${campaign._id}/recipients?limit=100`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('tkn')}` },
      });

      if (response.ok) {
        const data = await response.json();
        setRecipients(data.recipients || []);
      }
    } catch (error) {
      console.error('Error loading recipients:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'success';
      case 'delivered': return 'info';
      case 'opened': return 'primary';
      case 'clicked': return 'secondary';
      case 'bounced': return 'error';
      case 'unsubscribed': return 'warning';
      case 'failed': return 'error';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent': return <EmailIcon />;
      case 'delivered': return <CheckIcon />;
      case 'opened': return <ViewIcon />;
      case 'clicked': return <ClickIcon />;
      case 'bounced': return <BounceIcon />;
      case 'unsubscribed': return <UnsubscribeIcon />;
      case 'failed': return <ErrorIcon />;
      case 'pending': return <ScheduleIcon />;
      default: return <InfoIcon />;
    }
  };

  const calculateMetrics = () => {
    if (!analytics) return {};

    const total = analytics.sent || 0;
    const delivered = analytics.delivered || 0;
    const opened = analytics.opened || 0;
    const clicked = analytics.clicked || 0;
    const bounced = analytics.bounced || 0;
    const unsubscribed = analytics.unsubscribed || 0;
    const failed = analytics.failed || 0;

    return {
      deliveryRate: total > 0 ? (delivered / total) * 100 : 0,
      openRate: total > 0 ? (opened / total) * 100 : 0,
      clickRate: total > 0 ? (clicked / total) * 100 : 0,
      bounceRate: total > 0 ? (bounced / total) * 100 : 0,
      unsubscribeRate: total > 0 ? (unsubscribed / total) * 100 : 0,
      failureRate: total > 0 ? (failed / total) * 100 : 0,
      clickToOpenRate: opened > 0 ? (clicked / opened) * 100 : 0,
    };
  };

  const getStatusBreakdown = () => {
    const breakdown = {};
    recipients.forEach((recipient) => {
      breakdown[recipient.status] = (breakdown[recipient.status] || 0) + 1;
    });
    return breakdown;
  };

  const metrics = calculateMetrics();
  const statusBreakdown = getStatusBreakdown();

  if (!campaign) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Select a campaign to view analytics
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Campaign Analytics
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Performance metrics and detailed analysis for "
        {campaign.name}
        "
      </Typography>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <EmailIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {analytics?.sent?.toLocaleString() || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Sent
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <ViewIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {metrics.openRate.toFixed(1)}
                    %
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Open Rate
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <ClickIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="info.main">
                    {metrics.clickRate.toFixed(1)}
                    %
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click Rate
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <TrendingIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {metrics.clickToOpenRate.toFixed(1)}
                    %
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click-to-Open Rate
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Analytics Tabs */}
      <Paper elevation={2}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
            <Tab label="Overview" />
            <Tab label="Performance" />
            <Tab label="Recipients" />
            <Tab label="Timeline" />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <Grid container spacing={3}>
              {/* Delivery Metrics */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader title="Delivery Metrics" />
                  <CardContent>
                    <Stack spacing={2}>
                      <Box>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="body2">Delivery Rate</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {metrics.deliveryRate.toFixed(1)}
                            %
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={metrics.deliveryRate}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>

                      <Box>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="body2">Bounce Rate</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {metrics.bounceRate.toFixed(1)}
                            %
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={metrics.bounceRate}
                          color="error"
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>

                      <Box>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="body2">Failure Rate</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {metrics.failureRate.toFixed(1)}
                            %
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={metrics.failureRate}
                          color="error"
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Engagement Metrics */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader title="Engagement Metrics" />
                  <CardContent>
                    <Stack spacing={2}>
                      <Box>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="body2">Open Rate</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {metrics.openRate.toFixed(1)}
                            %
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={metrics.openRate}
                          color="success"
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>

                      <Box>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="body2">Click Rate</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {metrics.clickRate.toFixed(1)}
                            %
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={metrics.clickRate}
                          color="info"
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>

                      <Box>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="body2">Unsubscribe Rate</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {metrics.unsubscribeRate.toFixed(1)}
                            %
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={metrics.unsubscribeRate}
                          color="warning"
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Status Breakdown */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader title="Recipient Status Breakdown" />
                  <CardContent>
                    <Grid container spacing={2}>
                      {Object.entries(statusBreakdown).map(([status, count]) => (
                        <Grid item xs={12} sm={6} md={4} key={status}>
                          <Box sx={{
                            p: 2, border: 1, borderColor: 'divider', borderRadius: 1,
                          }}
                          >
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar sx={{
                                bgcolor: alpha(theme.palette[getStatusColor(status)].main, 0.1),
                                color: theme.palette[getStatusColor(status)].main,
                                width: 32,
                                height: 32,
                              }}
                              >
                                {getStatusIcon(status)}
                              </Avatar>
                              <Box>
                                <Typography variant="h6" fontWeight={600}>
                                  {count}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {status.charAt(0).toUpperCase() + status.slice(1)}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && (
            <Grid container spacing={3}>
              {/* Performance Comparison */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader title="Performance vs Goals" />
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" fontWeight={700} color="primary.main">
                            {metrics.openRate.toFixed(1)}
                            %
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Actual Open Rate
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Goal:
                            {' '}
                            {campaign.goals?.targetOpenRate || 0}
                            %
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" fontWeight={700} color="info.main">
                            {metrics.clickRate.toFixed(1)}
                            %
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Actual Click Rate
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Goal:
                            {' '}
                            {campaign.goals?.targetClickRate || 0}
                            %
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" fontWeight={700} color="success.main">
                            {metrics.clickToOpenRate.toFixed(1)}
                            %
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Click-to-Open Rate
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Industry Avg: ~3-5%
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Performance Insights */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader title="Performance Insights" />
                  <CardContent>
                    <List>
                      {metrics.openRate >= (campaign.goals?.targetOpenRate || 20) ? (
                        <ListItem>
                          <ListItemIcon>
                            <CheckIcon color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Open Rate Target Achieved"
                            secondary={`Your open rate of ${metrics.openRate.toFixed(1)}% meets or exceeds your target of ${campaign.goals?.targetOpenRate || 20}%`}
                          />
                        </ListItem>
                      ) : (
                        <ListItem>
                          <ListItemIcon>
                            <TrendingDownIcon color="warning" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Open Rate Below Target"
                            secondary={`Your open rate of ${metrics.openRate.toFixed(1)}% is below your target of ${campaign.goals?.targetOpenRate || 20}%`}
                          />
                        </ListItem>
                      )}

                      {metrics.clickRate >= (campaign.goals?.targetClickRate || 3) ? (
                        <ListItem>
                          <ListItemIcon>
                            <CheckIcon color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Click Rate Target Achieved"
                            secondary={`Your click rate of ${metrics.clickRate.toFixed(1)}% meets or exceeds your target of ${campaign.goals?.targetClickRate || 3}%`}
                          />
                        </ListItem>
                      ) : (
                        <ListItem>
                          <ListItemIcon>
                            <TrendingDownIcon color="warning" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Click Rate Below Target"
                            secondary={`Your click rate of ${metrics.clickRate.toFixed(1)}% is below your target of ${campaign.goals?.targetClickRate || 3}%`}
                          />
                        </ListItem>
                      )}

                      {metrics.bounceRate <= 5 ? (
                        <ListItem>
                          <ListItemIcon>
                            <CheckIcon color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Low Bounce Rate"
                            secondary={`Your bounce rate of ${metrics.bounceRate.toFixed(1)}% is excellent (below 5%)`}
                          />
                        </ListItem>
                      ) : (
                        <ListItem>
                          <ListItemIcon>
                            <BounceIcon color="error" />
                          </ListItemIcon>
                          <ListItemText
                            primary="High Bounce Rate"
                            secondary={`Your bounce rate of ${metrics.bounceRate.toFixed(1)}% is above the recommended 5%`}
                          />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {activeTab === 2 && (
            <Card>
              <CardHeader title="Recipient Details" />
              <CardContent>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Recipient</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Sent</TableCell>
                        <TableCell>Opened</TableCell>
                        <TableCell>Clicked</TableCell>
                        <TableCell>Last Activity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recipients.slice(0, 20).map((recipient, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Avatar sx={{ width: 24, height: 24 }}>
                                {recipient.contact?.name?.charAt(0) || recipient.email.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {recipient.contact?.name || 'Unknown'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {recipient.email}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={recipient.status}
                              color={getStatusColor(recipient.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {recipient.sentAt ? new Date(recipient.sentAt).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            {recipient.openedAt ? new Date(recipient.openedAt).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            {recipient.clickedAt ? new Date(recipient.clickedAt).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            {recipient.lastActivity ? new Date(recipient.lastActivity).toLocaleDateString() : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {recipients.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No recipient data available
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 3 && (
            <Card>
              <CardHeader title="Campaign Timeline" />
              <CardContent>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <ScheduleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Campaign Created"
                      secondary={new Date(campaign.createdAt).toLocaleString()}
                    />
                  </ListItem>

                  {campaign.schedule.type === 'scheduled' && campaign.schedule.scheduledAt && (
                    <ListItem>
                      <ListItemIcon>
                        <ScheduleIcon color="info" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Campaign Scheduled"
                        secondary={new Date(campaign.schedule.scheduledAt).toLocaleString()}
                      />
                    </ListItem>
                  )}

                  {campaign.lastSentAt && (
                    <ListItem>
                      <ListItemIcon>
                        <EmailIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Campaign Sent"
                        secondary={new Date(campaign.lastSentAt).toLocaleString()}
                      />
                    </ListItem>
                  )}

                  {campaign.updatedAt && (
                    <ListItem>
                      <ListItemIcon>
                        <AnalyticsIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Last Updated"
                        secondary={new Date(campaign.updatedAt).toLocaleString()}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default CampaignAnalytics;
